import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ArticleDetail, LocalizedContent } from '@/types/newsService';
import { withAlpha } from '@/lib/withAlpha';

interface Props {
  article: ArticleDetail;
  localized: LocalizedContent;
}

/**
 * Article hero — image with a dark gradient overlay, content-type badge,
 * primary tag chip, and title.
 *
 * Ported from mobile-app/src/components/news/ArticleHeader.tsx. The linear
 * gradient is implemented as a CSS overlay div instead of a separate
 * `LinearGradient` component.
 */
export const ArticleHeader = memo(function ArticleHeader({ article, localized }: Props) {
  const { t } = useTranslation('news');
  const tagBg = article.primaryTag?.color
    ? withAlpha(article.primaryTag.color, 0.13)
    : undefined;
  const tagFg = article.primaryTag?.color;

  return (
    <div>
      {article.heroImage && (
        <div className="relative">
          <img
            src={article.heroImage.url}
            alt={article.heroImage.alt}
            className="block h-[280px] w-full object-cover"
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[140px]"
            style={{
              background:
                'linear-gradient(to bottom, transparent 0%, rgba(16, 16, 16, 0.8) 60%, #101010 100%)',
            }}
          />
        </div>
      )}

      <div className="-mt-10 px-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.5px] text-fg-muted">
            {t(`contentType.${article.contentType}`, { defaultValue: article.contentType })}
          </span>
          {article.primaryTag && (
            <span
              className="rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.5px]"
              style={
                tagBg
                  ? { backgroundColor: tagBg, color: tagFg }
                  : { backgroundColor: 'rgba(0, 231, 223, 0.12)', color: '#00e7df' }
              }
            >
              {article.primaryTag.name}
            </span>
          )}
        </div>

        <h1 className="mb-2 text-2xl font-bold leading-8 text-fg">{localized.title}</h1>
      </div>
    </div>
  );
});
